import React, { useState } from "react";
import confetti from "canvas-confetti";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BellRing, Check, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";

export function FollowDialog({ open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      const { toast } = await import("sonner");
      toast.error("Please provide an email address");
      return;
    }
    setBusy(true);
    try {
      await api.post("/followers", {
        ...form,
        notify_email: true,
      });
      confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 } });
      const { toast } = await import("sonner");
      toast.success("You're now following the club!");
      setTimeout(() => onClose(), 1200);
    } catch (err) {
      const { toast } = await import("sonner");
      toast.error(err?.response?.data?.detail || "Sign up failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="p-0 w-[95vw] max-w-xl max-h-[90dvh] overflow-y-auto scrollbar-hide rounded-xl border-0 shadow-2xl bg-white text-foreground [&>button]:hidden"
        data-testid="follow-dialog"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Follow the club</DialogTitle>
        </DialogHeader>
        <div className="p-6 sm:p-8">
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Stay in the loop</div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-1">
            Follow the <span className="font-serif italic text-[hsl(var(--accent))]">club</span>.
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            Not ready to be a member? Subscribe to club updates — you'll receive email updates about matches, events, and news.
          </p>

          <form className="space-y-5" onSubmit={submit} onClick={(e) => e.stopPropagation()}>
            <Card className="p-5 rounded-2xl space-y-5">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Contact</div>
              <div>
                <Label htmlFor="dlg-follow-name" className="text-sm font-medium">
                  Name <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <Input
                  id="dlg-follow-name"
                  data-testid="follow-dlg-name"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  className="mt-1.5 h-11 rounded-lg"
                />
              </div>
              <div>
                <Label htmlFor="dlg-follow-email" className="text-sm font-medium">Email</Label>
                <Input
                  id="dlg-follow-email"
                  data-testid="follow-dlg-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  className="mt-1.5 h-11 rounded-lg"
                  placeholder="you@example.com"
                  required
                />
              </div>
            </Card>

            <Card className="p-5 rounded-2xl bg-[hsl(var(--accent))]/5 border-[hsl(var(--accent))]/30">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-[hsl(var(--accent))] text-white flex items-center justify-center flex-shrink-0">
                  <BellRing className="h-4 w-4" />
                </div>
                <div className="text-sm">
                  <div className="font-semibold tracking-tight">Email updates are on by default</div>
                  <div className="text-muted-foreground mt-1">
                    By following the club you'll receive announcements, match updates and event invites.
                  </div>
                  <ul className="mt-3 space-y-1.5 text-xs">
                    <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[hsl(var(--accent))]" /> Email updates</li>
                  </ul>
                </div>
              </div>
            </Card>

            <Button
              type="submit"
              disabled={busy}
              className="w-full rounded-full h-12 text-base font-semibold bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]/90"
              data-testid="follow-dlg-submit"
            >
              {busy ? "Saving…" : (<>Start following <ArrowUpRight className="ml-1.5 h-4 w-4" /></>)}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
